import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { TreeAccordion } from "client/Components";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { ActiveFilter } from "../common";
import { FollowCategory } from "./FollowCategory";
import { isNil, isNotNil } from "client/Misc/utils";
import { HubDataSource, HubItemDataSource } from "client/Api/Hub/hub";
import { ExpandCollapseNode } from "./ExpandCollapseNode";
import {
	HubItemCategoryNode,
	HubItemGroupNode,
	HubItemRootNode,
	HubSidebarValue
} from "./HubSidebar";
import { ItemContent } from "./ItemContent";

export type HubItemSidebarSectionProps = {
	selectedValue: HubSidebarValue | undefined;
	item: HubItemRootNode;
	collapsedItemsToDisplay: number;
	activeFilter: ActiveFilter | null;
	onSelect: (value: HubSidebarValue) => void;
	areActionsAvailable: boolean;
};

export const HubItemSidebarSection = (props: HubItemSidebarSectionProps): ReactElement => {
	const {
		selectedValue,
		item,
		collapsedItemsToDisplay,
		activeFilter,
		onSelect,
		areActionsAvailable
	} = props;

	const {
		children
	} = item;

	const [shouldShowAll, setShouldShowAll] = useState<boolean>(false);

	const [expandedItemIds, setExpandedItemIds] = useState<ReadonlySet<string>>(
		() => new Set<string>(initialExpandedItems)
	);

	const rootExpandId: string = useMemo(
		() => kindToExpandIdMap[item.source],
		[item.source]
	);

	useEffect(
		(): void => {
			if (isNil(selectedValue) || selectedValue.source !== item.source) {
				return;
			}

			const pathToSelectedCategory: readonly string[] = getParentNodeIds(
				selectedValue.categoryId,
				children
			);

			setExpandedItemIds(
				ids => {
					const newSet: Set<string> = new Set<string>(ids);
					newSet.add(rootExpandId);

					for (const nodeId of pathToSelectedCategory) {
						newSet.add(nodeId);
					}

					return newSet;
				}
			);

			setShouldShowAll(
				(shouldShowAll) => {
					return !shouldShowAll
						? children.some(
							(x, index) => (pathToSelectedCategory.includes(getItemId(x)) || x.id === selectedValue.categoryId)
								&& index >= collapsedItemsToDisplay
						)
						: shouldShowAll;
				}
			);
		},
		[
			children,
			collapsedItemsToDisplay,
			item.source,
			selectedValue,
			rootExpandId
		]
	);

	const tagPrefix: string = dataSourceToTagPrefixMap[item.source];

	const isExpanded = useCallback(
		(itemId: string): boolean => expandedItemIds.has(itemId),
		[expandedItemIds]
	);

	const toggleExpansionHandler = useCallback(
		(itemId: string): void => {
			setExpandedItemIds(ids => {
				const newIds: Set<string> = new Set(ids);

				if (!newIds.delete(itemId)) {
					newIds.add(itemId);
				}

				return newIds;
			});
		},
		[]
	);

	const isTagFilterActive: boolean = activeFilter?.kind === "tag";

	const displayedChildren = useMemo(
		(): readonly (HubItemCategoryNode | HubItemGroupNode)[] => children.slice(
			0,
			(shouldShowAll || isTagFilterActive)
				? children.length
				: collapsedItemsToDisplay
		),
		[
			children,
			shouldShowAll,
			isTagFilterActive,
			collapsedItemsToDisplay
		]
	);

	const shouldRenderMoreButton: boolean = !isTagFilterActive
		&& children.length > collapsedItemsToDisplay;

	const isRootExpanded: boolean = isExpanded(rootExpandId);

	return (
		<TreeAccordion.Node>
			<TreeAccordion.Item
				icon={item.icon}
				expansionToggle="visible"
				isExpanded={isRootExpanded}
				onClick={() => toggleExpansionHandler(rootExpandId)}
				onToggleExpansion={() => toggleExpansionHandler(rootExpandId)}
				trackingId={`${tagPrefix}-node`}
				title={item.name}
			>
				<ItemContent
					title={item.name}
					newItemsIndicator={!isRootExpanded
						? item.newItemsCount
						: undefined
					}
					isActive={false}
				/>
			</TreeAccordion.Item>
			<TreeAccordion.SubTree
				isCollapsed={!isRootExpanded}
				trackingId={`${tagPrefix}-sub-tree`}
			>
				{displayedChildren.map((x): ReactElement => {
					const nodeId: string = getItemId(x);

					return (
						<TreeAccordion.Node key={nodeId}>
							<Item
								item={x}
								source={item.source}
								selectedValue={selectedValue}
								tagPrefix={tagPrefix}
								indentionLevel={1}
								isExpanded={isExpanded}
								toggleExpansion={toggleExpansionHandler}
								onSelect={onSelect}
								areActionsAvailable={areActionsAvailable}
							/>
						</TreeAccordion.Node>
					);
				})}

				{shouldRenderMoreButton && (
					<ExpandCollapseNode
						isExpanded={shouldShowAll}
						onIsExpandedChange={setShouldShowAll}
						trackingId={`${tagPrefix}-expand`}
					/>
				)}
			</TreeAccordion.SubTree>
		</TreeAccordion.Node>
	);
};
HubItemSidebarSection.displayName = "HubItemSidebarSection";

type ItemProps = {
	source: HubDataSource.Announcement | HubDataSource.Event;
	item: HubItemCategoryNode | HubItemGroupNode;
	selectedValue: HubSidebarValue | undefined;
	tagPrefix: string;
	indentionLevel: number;
	isExpanded(id: string): boolean;
	toggleExpansion(id: string): void;
	onSelect: (value: HubSidebarValue) => void;
	areActionsAvailable: boolean;
};

const Item = (props: ItemProps): ReactElement => {
	const {
		source,
		item,
		selectedValue,
		tagPrefix,
		indentionLevel,
		isExpanded,
		toggleExpansion,
		onSelect,
		areActionsAvailable
	} = props;

	if (isGroupNode(item)) {
		const itemId: string = getItemId(item);

		return (
			<>
				<TreeAccordion.Item
					expansionToggle="visible"
					isExpanded={isExpanded(itemId)}
					onClick={() => toggleExpansion(itemId)}
					onToggleExpansion={() => toggleExpansion(itemId)}
					indentionLevel={indentionLevel}
					trackingId={`${tagPrefix}-${itemId}`}
					title={item.name}
				>
					<ItemContent
						title={item.name}
						newItemsIndicator={item.hasNewItems}
						isActive={false}
					/>
				</TreeAccordion.Item>
				<TreeAccordion.SubTree
					isCollapsed={!isExpanded(itemId)}
					trackingId={`${tagPrefix}-sub-tree|${itemId}`}
				>
					{item
						.children
						.map(
							x => {
								return (
									<TreeAccordion.Node key={getItemId(x)}>
										<Item
											source={source}
											item={x}
											selectedValue={selectedValue}
											tagPrefix={tagPrefix}
											indentionLevel={indentionLevel + 1}
											isExpanded={isExpanded}
											toggleExpansion={toggleExpansion}
											onSelect={onSelect}
											areActionsAvailable={areActionsAvailable}
										/>
									</TreeAccordion.Node>
								);
							}
						)}
				</TreeAccordion.SubTree>
			</>
		);
	}

	const {
		id,
		name
	} = item;

	const isSelected: boolean = isNotNil(selectedValue)
		&& selectedValue.source === source
		&& selectedValue.categoryId === id;

	return (
		<TreeAccordion.Item
			isSelected={isSelected}
			onClick={() => onSelect({ source, categoryId: id })}
			indentionLevel={indentionLevel}
			trackingId={`${tagPrefix}-${getItemId(item)}`}
			action={
				(areActionsAvailable && typeof item.id !== "undefined")
					? (
						<FollowCategory
							categoryId={item.id}
							categoryName={name}
							isFollowing={item.isFollowing}
						/>
					)
					: undefined
			}
			title={name}
		>
			<ItemContent
				title={name}
				newItemsIndicator={item.newItemsCount}
				isActive={isSelected}
			/>
		</TreeAccordion.Item>
	);

};
Item.displayName = "Item";


function getItemId(
	node: HubItemCategoryNode | HubItemGroupNode
): string {
	if (isGroupNode(node)) {
		return `group-${node.id}`;
	}

	return `category-${node.id ?? "all"}`;
}

function getParentNodeIds(
	selectedCategoryId: number | undefined,
	children: readonly (HubItemCategoryNode | HubItemGroupNode)[]
): readonly string[] {
	for (const child of children) {
		const result: (readonly string[]) | null = getParentNodeIdsRecursive(selectedCategoryId, child);

		if (result) {
			return result;
		}
	}

	return [];
}

function getParentNodeIdsRecursive(
	selectedCategoryId: number | undefined,
	node: HubItemCategoryNode | HubItemGroupNode
): (readonly string[]) | null {
	if (isGroupNode(node)) {
		for (const child of node.children) {
			const path: (readonly string[]) | null = getParentNodeIdsRecursive(selectedCategoryId, child);

			if (path) {
				return [getItemId(node), ...path];
			}
		}
	} else {
		if (node.id === selectedCategoryId) {
			return [];
		}
	}

	return null;
}

const dataSourceToTagPrefixMap: Record<HubItemDataSource, string> = {
	[HubDataSource.Announcement]: "announcement",
	[HubDataSource.Event]: "event",
	[HubDataSource.KnowledgeItem]: "knowledge-item"
};

const kindToExpandIdMap: Record<HubItemDataSource, string> = {
	[HubDataSource.Announcement]: "announcements",
	[HubDataSource.Event]: "event",
	[HubDataSource.KnowledgeItem]: "knowledge-item"
};

const initialExpandedItems: readonly string[] = [
	kindToExpandIdMap[HubItemKind.Announcement],
	kindToExpandIdMap[HubItemKind.Event],
	kindToExpandIdMap[HubItemKind.KnowledgeItem]
];

function isGroupNode(item: HubItemCategoryNode | HubItemGroupNode): item is HubItemGroupNode {
	return typeof (item as HubItemGroupNode).children !== "undefined";
}
